import {
  DatePicker,
  DateTimePicker,
  Input,
  ResponsiveGrid,
  Select,
} from "@marekl/adminer/components";
import type { RuleSetSummary } from "../../lib/queries/rules";
import type {
  CompetitionSeries,
  CompetitionType,
} from "../../lib/queries/competitions";
import { COMPETITION_TYPE_LABELS } from "../../lib/competitionState";
import type { CompetitionFormState } from "./competitionForm";

// The one competition form, shared by the admin list's create/edit
// modal and the competition-mode settings page so the field set cannot
// drift between the two surfaces. State type + converters live in
// competitionForm.ts.

interface CompetitionFormFieldsProps {
  form: CompetitionFormState;
  onChange: (form: CompetitionFormState) => void;
  ruleSets: RuleSetSummary[];
  series: CompetitionSeries[];
  // True once the competition left draft: the rule set is frozen (the
  // backend refuses the change; the season is pinned by the caller).
  lockRefs?: boolean;
}

export function CompetitionFormFields({
  form,
  onChange,
  ruleSets,
  series,
  lockRefs,
}: CompetitionFormFieldsProps) {
  return (
    <>
      <Input
        label="Název soutěže"
        required
        placeholder="Kvalifikační kolo MČR Hluk"
        value={form.name}
        onChange={(e) => onChange({ ...form, name: e.target.value })}
      />
      <Select
        label="Typ soutěže"
        value={form.type}
        onChange={(e) => onChange({ ...form, type: e.target.value as CompetitionType })}
      >
        {Object.entries(COMPETITION_TYPE_LABELS).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </Select>
      <ResponsiveGrid columns={2}>
        <DatePicker
          label="Datum konání"
          value={form.date}
          onChange={(v) => onChange({ ...form, date: v })}
        />
        <DatePicker
          label="Do (vícedenní)"
          value={form.dateTo}
          onChange={(v) => onChange({ ...form, dateTo: v })}
          minDate={form.date ?? undefined}
          hint="Prázdné = jednodenní soutěž."
        />
      </ResponsiveGrid>
      <Input
        label="Místo konání"
        placeholder="Sportovní hala Hluk"
        value={form.venue}
        onChange={(e) => onChange({ ...form, venue: e.target.value })}
      />
      <Select
        label="Sada pravidel"
        required
        value={form.ruleSetId}
        disabled={lockRefs}
        onChange={(e) => onChange({ ...form, ruleSetId: e.target.value })}
      >
        <option value="">— vyberte —</option>
        {ruleSets.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </Select>
      <Select
        label="Série"
        value={form.seriesId}
        onChange={(e) => onChange({ ...form, seriesId: e.target.value })}
      >
        <option value="">— bez série —</option>
        {series.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
        <option value="new">+ Nová série…</option>
      </Select>
      {form.seriesId === "new" && (
        <Input
          label="Název nové série"
          required
          placeholder="Série MČR 2026"
          value={form.newSeriesName}
          onChange={(e) => onChange({ ...form, newSeriesName: e.target.value })}
        />
      )}
      {/* The four uzávěrky get their own grid rather than sharing one
          with the capacity and the changeover: six cells in reading
          order would pair „Uzávěrka hudby" with „Max. kapacita" by
          accident. In ladder order, and each hint names the rung above
          it, which is exactly what an empty field falls back to. */}
      <ResponsiveGrid columns={2}>
        <DateTimePicker
          label="Uzávěrka přihlášek"
          value={form.entryDeadline}
          onChange={(v) => onChange({ ...form, entryDeadline: v })}
          hint="Povinná pro zveřejnění soutěže. Po ní tým novou přihlášku nepodá."
        />
        <DateTimePicker
          label="Uzávěrka změn kategorie"
          value={form.entryCategoryDeadline}
          onChange={(v) => onChange({ ...form, entryCategoryDeadline: v })}
          minDate={form.entryDeadline ?? undefined}
          hint="Do kdy smí tým u podané přihlášky změnit kombinaci. Prázdné = stejná jako uzávěrka přihlášek."
        />
        <DateTimePicker
          label="Uzávěrka změn soutěžících"
          value={form.entryRosterDeadline}
          onChange={(v) => onChange({ ...form, entryRosterDeadline: v })}
          minDate={form.entryCategoryDeadline ?? form.entryDeadline ?? undefined}
          hint="Do kdy smí tým měnit soutěžící a odhlásit se. Prázdné = stejná jako uzávěrka změn kategorie."
        />
        <DateTimePicker
          label="Uzávěrka hudby"
          value={form.musicDeadline}
          onChange={(v) => onChange({ ...form, musicDeadline: v })}
          hint="Prázdné = stejná jako uzávěrka přihlášek."
        />
      </ResponsiveGrid>
      <ResponsiveGrid columns={2}>
        <Input
          label="Max. kapacita přihlášek"
          type="number"
          min={1}
          value={form.maxCapacity}
          onChange={(e) => onChange({ ...form, maxCapacity: e.target.value })}
          hint="Prázdné = bez omezení."
        />
        <Input
          label="Prodleva mezi vystoupeními (s)"
          type="number"
          min={0}
          max={3600}
          value={form.startGapSeconds}
          onChange={(e) => onChange({ ...form, startGapSeconds: e.target.value })}
          hint="Čas na uklizení plochy a spuštění další hudby. Délka disciplíny ve startovní listině je počet startů × (časový limit + prodleva)."
        />
      </ResponsiveGrid>
      <Input
        label="Pořadatel"
        placeholder="Pořádající tým nebo organizace"
        value={form.organizerName}
        onChange={(e) => onChange({ ...form, organizerName: e.target.value })}
      />
      <ResponsiveGrid columns={3}>
        <Input
          label="Kontaktní osoba"
          value={form.contactName}
          onChange={(e) => onChange({ ...form, contactName: e.target.value })}
        />
        <Input
          label="Telefon"
          value={form.contactPhone}
          onChange={(e) => onChange({ ...form, contactPhone: e.target.value })}
        />
        <Input
          label="E-mail"
          value={form.contactEmail}
          onChange={(e) => onChange({ ...form, contactEmail: e.target.value })}
        />
      </ResponsiveGrid>
    </>
  );
}
